import { PencilSquareIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@moe/oss/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@moe/oss/ui/card";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@moe/oss/ui/form";
import { Textarea } from "@moe/oss/ui/textarea";
import { charPerToken } from "@moe/priv/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { TokenCounter } from "@web/components/TokenCounter";
import { useAppContext } from "@web/route-services/root/AppContext";
import { queryClient } from "@web/routes/__root";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const maxMemoryTokens = 2000;
const memoryFormSchema = z.object({
  memory: z
    .string()
    .max(maxMemoryTokens * charPerToken, `Memory box must have less than ${maxMemoryTokens} tokens.`)
    .optional()
});
type MemoryFormValues = z.infer<typeof memoryFormSchema>;

interface Props {
  chatID: number;
}

/**
 * A modal that allow you to add custom strings that will be injected into the system prompt as memory.
 */
export function MemoryBoxModal({ chatID }: Props) {
  const { access, closeModal } = useAppContext();

  const { data: chatData } = useQuery({
    queryKey: ["chat", chatID],
    queryFn: async () => {
      return await access.chat.getByID(chatID);
    }
  });

  const form = useForm<MemoryFormValues>({
    resolver: zodResolver(memoryFormSchema),
    values: chatData
  });

  const saveMutation = useMutation({
    mutationFn: async (values: MemoryFormValues) => {
      await access.chat.update({
        id: chatID,
        memory: values.memory
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chats", chatID] });
      toast.success("Saved!");
      closeModal();
    },
    onError: () => {
      toast.error("Failed to save memory box :<");
    }
  });

  const onSubmit = (values: MemoryFormValues) => {
    saveMutation.mutate(values);
  };

  return (
    <Card className="text-tx-primary bg-float scroll-primary max-h-[75vh] w-full overflow-y-auto border-none md:w-[32rem] md:border">
      <CardHeader>
        <CardTitle>Memory Box</CardTitle>
        <CardDescription>Customize the memory content for your chat</CardDescription>
      </CardHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent>
            <FormField
              control={form.control}
              name="memory"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="flex flex-col space-y-2">
                      <Textarea {...field} placeholder="Write things you want the character to remember here." />
                      <TokenCounter content={field.value || ""} className="text-sm" />
                      <FormMessage />
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter className="flex justify-end">
            <Button onClick={() => saveMutation.mutate} disabled={saveMutation.isPending}>
              <PencilSquareIcon />
              Save
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
